// src/pages/Positions.js
import React, { useState, useEffect } from 'react';
import { getPositions, getBars, STRATEGY_NAMES, getSymbolStrategy } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './Positions.css';

const Positions = () => {
  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [priceData, setPriceData] = useState([]);
  const [loading, setLoading] = useState(true);

  const PAGE_SIZE = 4;

  useEffect(() => {
    loadPositions();
    const interval = setInterval(loadPositions, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedPosition) {
      loadPriceData(selectedPosition.symbol);
    }
  }, [selectedPosition]);

  const loadPositions = async () => {
    try {
      const data = await getPositions();
      setPositions(data.positions);
      if (data.positions.length > 0 && !selectedPosition) {
        setSelectedPosition(data.positions[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading positions:', error);
      setLoading(false);
    }
  };

  const loadPriceData = async (symbol) => {
    try {
      const data = await getBars(symbol, 168); // 1 week
      const chartData = data.bars.map(bar => ({
        time: new Date(bar.timestamp).toLocaleTimeString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
        price: bar.close
      }));
      setPriceData(chartData);
    } catch (error) {
      console.error('Error loading price data:', error);
    }
  };

  // Filter positions by selected strategy
  const filteredPositions = selectedStrategy === 'all'
    ? positions
    : positions.filter(pos => {
        const strategy = getSymbolStrategy(pos.symbol);
        return strategy === selectedStrategy;
      });

  const totalPages = Math.max(1, Math.ceil(filteredPositions.length / PAGE_SIZE));
  const pagedPositions = filteredPositions.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Strategy colors
  const strategyColors = {
    vwap: '#00d4ff',
    meanrev: '#00ff88',
    momentum: '#ffd93d'
  };

  if (loading) {
    return <div className="loading">Loading positions...</div>;
  }

  if (positions.length === 0) {
    return (
      <div className="positions-empty">
        <h1>Open Positions</h1>
        <div className="empty-state">
          <span className="empty-icon">💼</span>
          <p>No open positions</p>
          <span className="empty-subtitle">Positions will appear here when trades are executed</span>
        </div>
      </div>
    );
  }

  if (filteredPositions.length === 0) {
    return (
      <div className="positions-empty">
        <div className="positions-header">
          <h1>Open Positions</h1>
          <div className="strategy-selector">
            <label>Strategy:</label>
            <select 
              value={selectedStrategy} 
              onChange={(e) => setSelectedStrategy(e.target.value)}
              className="strategy-dropdown"
            >
              <option value="all">{STRATEGY_NAMES.all}</option>
              <option value="vwap">{STRATEGY_NAMES.vwap}</option>
              <option value="meanrev">{STRATEGY_NAMES.meanrev}</option>
              <option value="momentum">{STRATEGY_NAMES.momentum}</option>
            </select>
          </div>
        </div>
        <div className="empty-state">
          <span className="empty-icon">💼</span>
          <p>No {STRATEGY_NAMES[selectedStrategy]} positions</p>
          <span className="empty-subtitle">
            Select "All Strategies" or wait for {STRATEGY_NAMES[selectedStrategy]} trades
          </span>
        </div>
      </div>
    );
  }

  const totalValue = filteredPositions.reduce((sum, pos) => sum + pos.market_value, 0);
  const totalPnL = filteredPositions.reduce((sum, pos) => sum + pos.unrealized_pl, 0);
  const totalPnLPercent = totalValue > 0 ? (totalPnL / (totalValue - totalPnL)) * 100 : 0;

  return (
    <div className="positions-page">
      <div className="positions-header">
        <h1>Open Positions</h1>
        
        <div className="header-controls">
          {/* Strategy Selector */}
          <div className="strategy-selector">
            <label>Strategy:</label>
            <select
              value={selectedStrategy}
              onChange={(e) => {
                const newStrategy = e.target.value;
                setSelectedStrategy(newStrategy);
                setCurrentPage(1);
                const newFiltered = newStrategy === 'all'
                  ? positions
                  : positions.filter(pos => getSymbolStrategy(pos.symbol) === newStrategy);
                setSelectedPosition(newFiltered.length > 0 ? newFiltered[0] : null);
              }}
              className="strategy-dropdown"
            >
              <option value="all">{STRATEGY_NAMES.all}</option>
              <option value="vwap">{STRATEGY_NAMES.vwap}</option>
              <option value="meanrev">{STRATEGY_NAMES.meanrev}</option>
              <option value="momentum">{STRATEGY_NAMES.momentum}</option>
            </select>
          </div>

          {/* Summary Stats */}
          <div className="positions-summary">
            <div className="summary-item">
              <span className="summary-label">Total Value</span>
              <span className="summary-value">${totalValue.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total P&L</span>
              <span className={`summary-value ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
                {totalPnL >= 0 ? '+' : ''}${totalPnL.toFixed(2)} ({totalPnLPercent >= 0 ? '+' : ''}{totalPnLPercent.toFixed(2)}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="positions-content">
        <div className="positions-list">
          {pagedPositions.map((pos) => {
            const strategy = getSymbolStrategy(pos.symbol);
            return (
              <div
                key={pos.symbol}
                className={`position-card ${selectedPosition?.symbol === pos.symbol ? 'active' : ''}`}
                onClick={() => setSelectedPosition(pos)}
              >
                <div className="position-header">
                  <div className="position-title">
                    <span className="position-symbol">{pos.symbol}</span>
                    <span
                      className="position-strategy"
                      style={{color: strategyColors[strategy]}}
                    >
                      {STRATEGY_NAMES[strategy]}
                    </span>
                  </div>
                  <span className={`position-pnl ${pos.unrealized_pl >= 0 ? 'positive' : 'negative'}`}>
                    {pos.unrealized_pl >= 0 ? '+' : ''}${pos.unrealized_pl.toFixed(2)}
                  </span>
                </div>
                <div className="position-details">
                  <div className="detail-row">
                    <span className="detail-label">Qty</span>
                    <span className="detail-value">{pos.qty}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Entry</span>
                    <span className="detail-value">${pos.avg_entry_price.toFixed(2)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Current</span>
                    <span className="detail-value">${pos.current_price.toFixed(2)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Return</span>
                    <span className={`detail-value ${pos.unrealized_plpc >= 0 ? 'positive' : 'negative'}`}>
                      {(pos.unrealized_plpc * 100).toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          {totalPages > 1 && (
            <div className="positions-pagination">
              <button
                className="page-btn"
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`page-btn ${page === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className="page-btn"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>
          )}
        </div>

        {selectedPosition && (
          <div className="position-detail">
            <div className="detail-header">
              <div className="detail-title">
                <h2>{selectedPosition.symbol}</h2>
                <span 
                  className="detail-strategy"
                  style={{color: strategyColors[getSymbolStrategy(selectedPosition.symbol)]}}
                >
                  {STRATEGY_NAMES[getSymbolStrategy(selectedPosition.symbol)]}
                </span>
              </div>
              <div className={`detail-pnl ${selectedPosition.unrealized_pl >= 0 ? 'positive' : 'negative'}`}>
                {selectedPosition.unrealized_pl >= 0 ? '+' : ''}${selectedPosition.unrealized_pl.toFixed(2)} 
                ({(selectedPosition.unrealized_plpc * 100).toFixed(2)}%)
              </div>
            </div>

            <div className="detail-stats">
              <div className="stat-item">
                <span className="stat-label">Quantity</span>
                <span className="stat-value">{selectedPosition.qty} shares</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Entry Price</span>
                <span className="stat-value">${selectedPosition.avg_entry_price.toFixed(2)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Current Price</span>
                <span className="stat-value">${selectedPosition.current_price.toFixed(2)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Market Value</span>
                <span className="stat-value">${selectedPosition.market_value.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Cost Basis</span>
                <span className="stat-value">${selectedPosition.cost_basis.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Today's Change</span>
                <span className={`stat-value ${selectedPosition.change_today >= 0 ? 'positive' : 'negative'}`}>
                  {selectedPosition.change_today >= 0 ? '+' : ''}{(selectedPosition.change_today * 100).toFixed(2)}%
                </span>
              </div>
            </div>

            <div className="price-chart">
              <h3>Price (Last 7 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={priceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a2332" />
                  <XAxis dataKey="time" stroke="#8b92a7" tick={{fontSize: 11}} />
                  <YAxis stroke="#8b92a7" domain={['auto', 'auto']} />
                  <Tooltip 
                    contentStyle={{ background: '#0f1729', border: '1px solid #1a2332', borderRadius: '8px' }}
                    labelStyle={{ color: '#8b92a7' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="price" 
                    stroke={strategyColors[getSymbolStrategy(selectedPosition.symbol)]}
                    strokeWidth={2} 
                    dot={false}
                    animationDuration={300}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Positions;